/**
 * Hook personalizado para gestión de permisos
 * Facilita la verificación de permisos en componentes React
 */
import { useState, useEffect, useCallback } from 'react';
import permisosService from '../services/permisos.service';

/**
 * Hook para verificar permisos del usuario
 * @returns {Object} - { permisos, tienePermiso, tieneAlgunPermiso, tieneTodosPermisos, loading, recargar }
 * 
 * @example
 * const { tienePermiso, loading } = usePermisos();
 * 
 * if (loading) return <Spinner />;
 * if (!tienePermiso('CARGAINF')) return <Redirect to="/home" />;
 * 
 * return <FormularioInforme />;
 */
export const usePermisos = () => {
  const [permisos, setPermisos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cargarPermisos = useCallback(async () => {
    try {
      setLoading(true);
      const misPermisos = await permisosService.getMyPermisos();
      console.log('🔑 Hook usePermisos - Permisos cargados:', misPermisos);
      setPermisos(misPermisos);
      setError(null);
    } catch (err) {
      console.error('Error al cargar permisos:', err);
      setError(err);
      setPermisos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarPermisos();
  }, [cargarPermisos]);

  /**
   * Verificar si tiene un permiso específico
   */
  const tienePermiso = useCallback((permiso) => {
    return permisos.includes(permiso);
  }, [permisos]);

  /**
   * Verificar si tiene al menos uno de los permisos
   */
  const tieneAlgunPermiso = useCallback((permisosRequeridos) => {
    return permisosRequeridos.some(p => permisos.includes(p));
  }, [permisos]);

  /**
   * Verificar si tiene todos los permisos
   */
  const tieneTodosPermisos = useCallback((permisosRequeridos) => {
    return permisosRequeridos.every(p => permisos.includes(p));
  }, [permisos]);

  return {
    permisos,
    tienePermiso,
    tieneAlgunPermiso,
    tieneTodosPermisos,
    loading,
    error,
    recargar: cargarPermisos
  };
};

/**
 * Hook simple para verificar un permiso específico
 * @param {string} permiso - Permiso a verificar
 * @returns {Object} - { autorizado, loading }
 * 
 * @example
 * const { autorizado, loading } = usePermiso('CARGAINF');
 * 
 * if (loading) return <Spinner />;
 * if (!autorizado) return <p>No autorizado</p>;
 * 
 * return <Componente />;
 */
export const usePermiso = (permiso) => {
  const { tienePermiso, loading } = usePermisos();
  const autorizado = tienePermiso(permiso);

  return { autorizado, loading };
};

/**
 * Hook para cargar todos los permisos disponibles (admin)
 * @returns {Object} - { permisos, loading, error, recargar }
 */
export const useTodosLosPermisos = () => {
  const [permisos, setPermisos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    try {
      setLoading(true);
      const data = await permisosService.getAllPermisos();
      setPermisos(data);
      setError(null);
    } catch (err) {
      console.error('Error al cargar todos los permisos:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  return { permisos, loading, error, recargar: cargar };
};

/**
 * Hook para cargar roles con sus permisos (admin)
 * @returns {Object} - { roles, loading, error, recargar }
 */
export const useRolesConPermisos = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    try {
      setLoading(true);
      const data = await permisosService.getAllRolesWithPermisos();
      setRoles(data);
      setError(null);
    } catch (err) {
      console.error('Error al cargar roles con permisos:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  return { roles, loading, error, recargar: cargar };
};

export default usePermisos;
